import { useMemo, useState, type JSX, type ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { NotificationIcon } from '@dynatrace/strato-icons';

import { Menu } from '../../../navigation/menu/Menu.js';
import { Modal } from '../../../overlays/modal/Modal.js';
import { isEventNotificationConfig } from '../components/EventNotificationClient.js';
import { isResourceNotificationConfig } from '../components/ResourceNotificationClient.js';
import { notificationMessages } from '../messages.js';
import {
  NotificationSettings,
  type NotificationSettingsProps,
} from '../NotificationSettings.js';

/**
 * Props for the {@link useNotificationSettingsMenuItem} hook
 * @public
 */
export type UseNotificationSettingsMenuItemProps = Pick<
  NotificationSettingsProps,
  'type' | 'config'
> & {
  /** Either a ReactNode or a render function that gets passed certain values from the currenlty configured notification */
  content?:
    | (({
        disabled,
        headingText,
        infoText,
        hasReadPermission,
        hasWritePermission,
      }: {
        disabled: boolean;
        headingText: string;
        infoText: string;
        hasReadPermission: boolean;
        hasWritePermission: boolean;
      }) => ReactNode)
    | ReactNode;
};

/**
 * Helper hook to use a NotificationSetting within a Menu.
 * @public */
export function useNotificationSettingsMenuItem(
  props: UseNotificationSettingsMenuItemProps,
): [() => JSX.Element, () => JSX.Element] {
  const { type, config, content } = props;

  const [modalOpen, setModalOpen] = useState(false);

  const NotificationMenuItem = useMemo<() => JSX.Element>(() => {
    return () => (
      <Menu.Item onSelect={() => setModalOpen(true)}>
        <Menu.ItemIcon>
          <NotificationIcon />
        </Menu.ItemIcon>
        <FormattedMessage {...notificationMessages.menuItemLabel} />
      </Menu.Item>
    );
  }, []);

  const NotificationSettingsModal = useMemo<() => JSX.Element>(() => {
    let notificationSettings: ReactNode = null;

    if (isResourceNotificationConfig(type, config)) {
      notificationSettings = (
        <NotificationSettings type="resource" config={config}>
          {content}
        </NotificationSettings>
      );
    } else if (isEventNotificationConfig(type, config)) {
      notificationSettings = (
        <NotificationSettings type="event" config={config}>
          {content}
        </NotificationSettings>
      );
    }

    return () => (
      <Modal
        show={modalOpen}
        size="small"
        onDismiss={() => setModalOpen(false)}
        title={
          <FormattedMessage
            {...notificationMessages.notificationSettingsModalTitle}
          />
        }
      >
        {notificationSettings}
      </Modal>
    );
  }, [config, content, modalOpen, type]);

  return [NotificationMenuItem, NotificationSettingsModal] as const;
}
